import { builder, Builder } from '@builder.io/react';
import { useRouter } from 'next/router';
import Error from 'next/error';
import NoSSR from 'react-no-ssr';

import { Header, Footer, Navigation } from 'react-scale/components';
import Hero from 'react-scale/components/pages/customers/Hero';
import CustomerSection from 'react-scale/components/pages/customers/CustomerSection';
import Quote from 'react-scale/components/pages/customers/Quote';
import Slider from 'react-scale/components/pages/customers/Slider';
import TryBanner from 'react-scale/components/sections/cta/simple-centered';

const CustomersArticle = ({ article }) => {
  const router = useRouter();
  if (router.isFallback) {
    return <h1>Loading...</h1>;
  }

  const isLive = !Builder.isEditing && !Builder.isPreviewing;
  if (!article && isLive) {
    return (
      <NoSSR>
        <Error statusCode={404} />
      </NoSSR>
    );
  }

  return (
    <>
      <Header
        title={article?.data?.title}
        canonical={`https://scale.com/customers/${article?.data?.slug}`}
        twImg={
          article?.data?.twImg
            ? article?.data?.twImg
            : 'https://scale.com/static/images/global/twitter.png'
        }
        ogImg={
          article?.data?.ogImg
            ? article?.data?.ogImg
            : 'https://scale.com/static/images/global/facebook.png'
        }
        theme="light"
        background="bg-neutral-100"
      />
      <Navigation
        theme="light"
        title={{
          text: 'Customers'
        }}
        links={[
          {
            text: 'Overview',
            href: '#overview'
          },
          {
            text: 'The Problem',
            href: '#problem'
          },
          {
            text: 'The Solution',
            href: '#solution'
          },
          {
            text: 'The Result',
            href: '#result'
          }
        ]}
        href="/customers"
        initialBackground="#f5f5f5"
      />
      {article?.data?.hero && (
        <Hero
          logo={article.data.logo.value.data}
          text={article.data.hero.subtitle}
          cta={{
            buttons:
              article.data.hero.cta && article.data.hero.cta?.text !== ''
                ? [article.data.hero.cta]
                : [cta]
          }}
          image={article.data.indexList.coverImage}
          video={article.data.sectionOverview?.video}
          info={article.data.sectionOverview?.info}
        />
      )}
      {article?.data?.sectionOverview && (
        <CustomerSection
          id="overview"
          overtitle="Overview"
          title={article.data.sectionOverview?.title}
          text={article.data.sectionOverview?.text}
          paddingBottom={false}
          paddingTop
          border={false}
          info={article.data.sectionOverview?.info}
        />
      )}
      {article?.data?.sectionProblem && (
        <CustomerSection
          id="problem"
          overtitle="The Problem"
          title={article.data.sectionProblem?.title}
          text={article.data.sectionProblem?.text}
          paddingBottom={false}
          paddingTop
          border
        />
      )}
      {article?.data?.topQuote?.value && (
        <Quote author={article.data.topQuote.value.data} paddingTop paddingBottom={false} />
      )}
      {article?.data?.sectionSolution && (
        <CustomerSection
          id="solution"
          overtitle="The Solution"
          title={article.data.sectionSolution?.title}
          text={article.data.sectionSolution?.text}
          lidar={article.data.sectionSolution?.lidar}
          caption={article.data.sectionSolution?.caption}
          paddingBottom={!article.data.middleQuote?.value}
          paddingTop
          border
        />
      )}
      {article?.data?.middleQuote?.value && (
        <Quote author={article.data.middleQuote.value.data} paddingTop paddingBottom />
      )}
      {article?.data?.slides && <Slider slides={article.data.slides} />}
      {article?.data?.sectionResult && (
        <CustomerSection
          id="result"
          overtitle="The Result"
          title={article.data.sectionResult?.title}
          text={article.data.sectionResult?.text}
          paddingBottom={!article.data.bottomQuote?.value}
          paddingTop={!article.data.slides}
          border={!article.data.slides}
        />
      )}
      {article?.data?.bottomQuote?.value && (
        <Quote author={article.data.bottomQuote.value.data} paddingTop paddingBottom />
      )}
      <TryBanner
        theme="light"
        border
        cta={{
          buttons:
            article.data.tryBanner?.cta && article.data.tryBanner.cta?.text !== ''
              ? article.data.tryBanner.cta
              : tryBannerCta
        }}
      />
      <Footer theme="light" border />
    </>
  );
};

CustomersArticle.theme = 'light';

const tryBannerCta = [
  {
    text: 'Get Started',
    href: '/rapid',
    type: 'primary',
    variant: 'black'
  },
  {
    text: 'Book a Demo',
    href: '/sales'
  }
];

const cta = {
  text: 'Contact Sales →',
  href: '/sales',
  type: 'primary'
};

export const getStaticProps = async ({ params }) => {
  const article =
    (await builder
      .get('customers-cms', {
        options: { noTargeting: true, includeUnpublished: true },
        query: { 'data.slug': params.slug },
        limit: 200
      })
      .toPromise()) || null;

  return {
    props: {
      article
    },
    revalidate: 1
  };
};

export const getStaticPaths = async () => {
  const customers = await builder.getAll('customers-cms', {
    options: { noTargeting: true },
    omit: 'data.blocks',
    limit: 200
  });
  const paths = customers.map(customer => ({ params: { slug: `${customer.data.slug}` } }));

  return {
    paths,
    fallback: 'blocking'
  };
};

export default CustomersArticle;
